import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import Badge from '../../../components/ui/Badge';

const focusInput =
  'w-full rounded-[var(--radius-md)] border border-[var(--color-gray-300)] bg-white text-[var(--color-gray-800)] focus:outline-none focus:border-[var(--color-primary-light)] focus:ring-2 focus:ring-[var(--color-primary-50)]';

// ── Page header ──────────────────────────────────────────────────────────────
export const PageHeader: React.FC<{ title: string; subtitle?: string; actions?: React.ReactNode }> = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between gap-4 mb-5">
    <div>
      <h1 className="text-[var(--font-size-2xl)] font-bold text-[var(--color-gray-900)] leading-tight">{title}</h1>
      {subtitle && <p className="text-sm text-[var(--color-gray-500)] mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

// ── Card ─────────────────────────────────────────────────────────────────────
export const Card: React.FC<{ children: React.ReactNode; className?: string; title?: React.ReactNode; actions?: React.ReactNode }> = ({ children, className = '', title, actions }) => (
  <div className={`card ${className}`}>
    {(title || actions) && (
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-gray-100)]">
        {typeof title === 'string' ? <h3 className="text-sm font-semibold text-[var(--color-gray-700)] uppercase tracking-wide">{title}</h3> : title}
        {actions}
      </div>
    )}
    {children}
  </div>
);

// ── Search box ───────────────────────────────────────────────────────────────
export const SearchBox: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; className?: string }> = ({ value, onChange, placeholder = 'Search…', className = '' }) => (
  <div className={`relative ${className}`}>
    <input
      className={`${focusInput} !pl-3`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

// ── Modal ────────────────────────────────────────────────────────────────────
export const Modal: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
}> = ({ open, title, onClose, children, footer, width = 'md' }) => {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  const maxW = width === 'lg' ? 'max-w-3xl' : width === 'sm' ? 'max-w-md' : 'max-w-xl';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 sm:p-8" onMouseDown={onClose}>
      <div className={`w-full ${maxW} max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] bg-white rounded-[var(--radius-lg)] shadow-xl flex flex-col`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-gray-100)]">
          <h2 className="text-[var(--font-size-lg)] font-semibold text-[var(--color-gray-900)]">{title}</h2>
          <button onClick={onClose} className="text-[var(--color-gray-400)] hover:text-[var(--color-gray-700)] p-1 rounded hover:bg-[var(--color-gray-100)]">
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--color-gray-100)] bg-[var(--color-gray-50)] rounded-b-[var(--radius-lg)]">{footer}</div>}
      </div>
    </div>
  );
};

// ── Labeled field wrapper ────────────────────────────────────────────────────
export const Field: React.FC<{ label: string; required?: boolean; hint?: string; children: React.ReactNode; className?: string }> = ({ label, required, hint, children, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-[var(--color-gray-700)] mb-1">
      {label}
      {required && <span className="text-[var(--color-error)]"> *</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-[var(--color-gray-500)] mt-1">{hint}</p>}
  </div>
);

export const inputClass = focusInput;

// ── Status pill ──────────────────────────────────────────────────────────────
export const StatusPill: React.FC<{ active: boolean; activeLabel?: string; inactiveLabel?: string }> = ({ active, activeLabel = 'Active', inactiveLabel = 'Inactive' }) => (
  <Badge color={active ? 'success' : 'neutral'} variant="soft">{active ? activeLabel : inactiveLabel}</Badge>
);

// ── Client-side search + pagination helper ───────────────────────────────────
export function usePagedList<T>(items: T[], predicate: (item: T, q: string) => boolean, pageSize = 10) {
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(1);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((it) => predicate(it, q)) : items;
  }, [items, query, predicate]);

  React.useEffect(() => {
    setPage(1);
  }, [query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return {
    query,
    setQuery,
    page: safePage,
    setPage,
    pageItems,
    pagination: { page: safePage, limit: pageSize, total, totalPages },
  };
}
